import { Link } from 'react-router-dom';

export default function Terms() {
  const lastUpdated = 'April 2026';

  return (
    <div className="container py-5" style={{ maxWidth: 880 }}>
      <div className="mb-4">
        <Link to="/" className="text-decoration-none small text-muted">
          <i className="fas fa-arrow-left me-1"></i>Back to home
        </Link>
      </div>

      <h1 className="fw-bold mb-2">Terms and Conditions</h1>
      <p className="text-muted small">Last updated: {lastUpdated}</p>

      <div style={{ lineHeight: 1.7, color: 'var(--ink)' }}>
        <Section title="1. About this service">
          <p>
            Dissertation Island is an academic writing assistant that drafts dissertations,
            assignments, and time-series analyses on the topic you provide. The platform
            generates a complete first draft using publicly available academic literature and
            secondary data sources. By creating an account or using the service you agree to
            these Terms.
          </p>
        </Section>

        <Section title="2. The output is a draft, not a finished submission">
          <p>
            Every document produced by Dissertation Island is a <strong>draft</strong>. You are
            expected to review, fact-check, edit, and where necessary substantially revise the
            output before submitting it for academic credit or publication. Specifically:
          </p>
          <ul>
            <li>
              <strong>Verify every citation.</strong> The platform draws on real literature,
              but you should confirm that each cited source exists, that the author and year
              are correct, and that the claim attributed to the source is accurate.
            </li>
            <li>
              <strong>Check the data.</strong> Time-series and statistical results are computed
              from public datasets at the time of generation. Re-run or re-verify any number you
              intend to defend.
            </li>
            <li>
              <strong>Adapt the framing.</strong> Sections such as the introduction, ethics,
              methodology, and conclusion are written generically and may need to be tailored to
              your institution, course, supervisor's preferences, and country context.
            </li>
            <li>
              <strong>Edit the prose.</strong> Read the document end-to-end, fix anything that
              does not match your voice or your study, and rewrite paragraphs that do not say
              what you would say.
            </li>
          </ul>
          <p>
            The platform is a tool to accelerate your draft, not a substitute for your own
            scholarship. Final responsibility for accuracy, originality, and submission-readiness
            lies with you.
          </p>
        </Section>

        <Section title="3. Academic integrity">
          <p>
            You are responsible for understanding and complying with the academic integrity
            policies of your institution. Many universities require disclosure when AI tools are
            used in coursework, and some prohibit their use altogether. Before submitting any
            output:
          </p>
          <ul>
            <li>Check your institution's policy on AI-assisted writing.</li>
            <li>
              Disclose your use of Dissertation Island where required by your supervisor,
              department, or examination board.
            </li>
            <li>
              Do not submit drafts unchanged. The output is intended as a starting point for
              your own writing, not as the final word.
            </li>
          </ul>
          <p>
            Dissertation Island is not responsible for academic-misconduct findings that result
            from misuse of the platform.
          </p>
        </Section>

        <Section title="4. No guarantee of correctness">
          <p>
            We make reasonable efforts to ground generated content in real literature and
            authentic public data, but we do not guarantee that every statement, citation,
            statistic, or interpretation is correct. The service is provided on an
            "as-is" basis without warranties of any kind. You use the output at your own
            risk and must independently verify anything you rely on.
          </p>
        </Section>

        <Section title="5. Acceptable use">
          <p>You agree that you will not use the service to:</p>
          <ul>
            <li>
              Submit unedited output for academic credit in violation of your institution's
              policy.
            </li>
            <li>Generate content that is illegal, defamatory, harassing, or harmful.</li>
            <li>
              Reverse-engineer, scrape, resell, or otherwise exploit the platform outside the
              scope of a personal account.
            </li>
            <li>Misrepresent the authorship of the document to any party.</li>
          </ul>
        </Section>

        <Section title="6. Credits and payments">
          <p>
            Generation requires credits, which are purchased through the Pricing page. Credits
            are consumed when a generation job starts. Failed generations caused by a platform
            error are refunded as credits to your account; charges from generations completed
            successfully are non-refundable. Pricing may change with notice.
          </p>
        </Section>

        <Section title="7. Your data">
          <p>
            We store the topic, generation parameters, and resulting documents under your
            account so you can revisit and download them. We do not sell your data. Email
            addresses are used only for sign-in and service notifications. You may request
            deletion of your account and associated documents by emailing
            <a href="mailto:silas.phiri21@gmail.com" className="ms-1">silas.phiri21@gmail.com</a>.
          </p>
        </Section>

        <Section title="8. Intellectual property">
          <p>
            You retain ownership of the topic and parameters you submit, and of the document
            produced for your account. The underlying platform, prompts, model orchestration,
            and analysis pipeline remain the intellectual property of Dissertation Island.
          </p>
        </Section>

        <Section title="9. Limitation of liability">
          <p>
            To the maximum extent permitted by law, Dissertation Island, its operators, and its
            contributors are not liable for any indirect, incidental, or consequential damages
            arising from your use of the service, including but not limited to academic
            penalties, lost opportunities, lost time, or reputational harm. Total aggregate
            liability is limited to the amount you have paid for credits in the twelve months
            preceding the claim.
          </p>
        </Section>

        <Section title="10. Changes to these Terms">
          <p>
            We may update these Terms from time to time. The "last updated" date at the top of
            this page reflects the most recent revision. Continued use of the service after a
            revision constitutes acceptance of the updated Terms.
          </p>
        </Section>

        <Section title="11. Contact">
          <p>
            Questions, complaints, or data-deletion requests:{' '}
            <a href="mailto:silas.phiri21@gmail.com">silas.phiri21@gmail.com</a>.
          </p>
        </Section>

        <div
          className="alert"
          style={{
            background: '#fef3c7',
            border: '1px solid #fde68a',
            color: '#92400e',
            marginTop: '2rem',
          }}
        >
          <strong>In one line:</strong> Dissertation Island writes a complete first draft for
          you. Reviewing it, verifying every citation and number, and shaping it into something
          you would actually submit is your job.
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section style={{ marginTop: '1.8rem' }}>
      <h3 className="fw-semibold" style={{ fontSize: '1.15rem', marginBottom: '0.6rem' }}>
        {title}
      </h3>
      {children}
    </section>
  );
}
